import { Dispatch } from 'redux';

import { post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import CustomAppTypes from '../config/customAppTypes';
import { AppState } from '../reducers';
import { errorNames } from '../util/mappings/errorName';
import { customFailed } from '../util/navUtil';
import { trackEvent, TrackingStep } from '../util/piwik-utils';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;
type SendApplicationResponse = import('../generated-types').SoknadResponse;

export interface SendApplicationStartedAction extends Action<typeof actionTypes.SEND_APPLICATION_STARTED> {
    type: typeof actionTypes.SEND_APPLICATION_STARTED;
}

export interface SendApplicationFulfilledAction extends Action<typeof actionTypes.SEND_APPLICATION_FULFILLED> {
    type: typeof actionTypes.SEND_APPLICATION_FULFILLED;
    payload: SendApplicationResponse;
}

export interface SendApplicationFailedAction extends Action<typeof actionTypes.SEND_APPLICATION_FAILED> {
    type: typeof actionTypes.SEND_APPLICATION_FAILED;
    payload: RequestExceptionWithResponse;
}

export type SendApplicationActions =
    | SendApplicationStartedAction
    | SendApplicationFulfilledAction
    | SendApplicationFailedAction;

export const sendApplicationStarted = (): SendApplicationStartedAction => ({
    type: actionTypes.SEND_APPLICATION_STARTED,
});
export const sendApplicationFulfilled = (json: SendApplicationResponse): SendApplicationFulfilledAction => ({
    type: actionTypes.SEND_APPLICATION_FULFILLED,
    payload: json,
});

export const sendApplicationFailed = (error: RequestExceptionWithResponse): SendApplicationFailedAction => ({
    type: actionTypes.SEND_APPLICATION_FAILED,
    payload: error,
});

export const sendApplication = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { loader, cards, application, context, naf, lbs } = getState();
    const { hasSentApplication, isSendingApplication } = loader;
    const { kortId, korttype } = cards;
    const {
        epost,
        mobiltelefon,
        sivilstatus,
        arbeidsforhold,
        annensikretgjeld,
        kredittgrense,
        boforhold,
        harAndreKort,
        custom,
        requestedCreditAmount,
    } = application;

    if (!kredittgrense && !hasSentApplication && !isSendingApplication) {
        dispatch(sendApplicationStarted());

        let medlemsnummer = null;
        if (CustomAppTypes.isNaf(context)) {
            medlemsnummer = naf.medlemsnummer || null;
        }

        const data = {
            epost,
            mobil: mobiltelefon,
            bankregnr: {
                value: CustomAppTypes.isLbs(context) && lbs.lbsBankregnr ? lbs.lbsBankregnr : context.config.bankregnr,
            },
            medlemsnummer,
            sivilstatus: Number(sivilstatus),
            arbeidsforhold: Number(arbeidsforhold),
            boforhold: Number(boforhold),
            egenSikretGjeld: Number(annensikretgjeld),
            harAndreKort: Number(harAndreKort),
            requestedCreditAmount: Number(requestedCreditAmount),
            kort: {
                produkt: {
                    kode: korttype,
                    gruppe: null,
                },
                kontonummer: kortId || null,
            },
            agricard: custom.agricard,
        };

        try {
            dispatch(sendApplicationStarted());
            const response: SendApplicationResponse = await post(
                `${actionTypes.RESOURCE_PATH}/application/init`,
                data,
                getRequestHeaders(context.config.bankregnr),
            );
            trackEvent(TrackingStep.SendApplication);
            switch (response.status) {
                case 'INNVILGET':
                case 'TIL_SIGNERING':
                    trackEvent(TrackingStep.ApplicationApproved);
                    break;
                case 'KONTROLL':
                    trackEvent(TrackingStep.ApplicationControl);
                    break;
                case 'AVSLAATT':
                    trackEvent(TrackingStep.ApplicationRejected);
                    break;
                default:
                    break;
            }
            dispatch(sendApplicationFulfilled(response));
        } catch (error) {
            if (CustomAppTypes.isAgri(context) && error.errorName === 'UGYLDIG_PRODUKT') {
                dispatch(sendApplicationFailed(customFailed(errorNames.agriTokenValidFailed)));
            } else {
                dispatch(sendApplicationFailed(error));
            }
        }
    }
};
